import { postServiceRequest } from "../../../app/api/getServiceRequestCategories";
import { BaseAction } from "../../../core/actions/BaseAction";
import { isActionRunSuccessfully, isEmpty } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { ISaveServiceRequestActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/ServiceRequestActionParams";
import { IAdditionalField, ISaveServiceRequestPayload, ISaveServiceRequestResponse } from "../../../Orchestrator/Types/Interface/IServiceRequest";
import { IPassengerSelection } from "../../../Orchestrator/Types/Interface/IPassenger";
import { checkRequiredValidationAction } from "./CheckRequiredValidationAction";

class SaveServiceRequestAction extends BaseAction<IDefaultActionResult, ISaveServiceRequestActionParams> {

    override async run(): Promise<IDefaultActionResult> {
        let actionResult: IDefaultActionResult = {
            data: [],
            message: "",
            status: "fail"
        }
        try {
            const { formData, selectedCategoryAndSubCategory, bookingId } = this.params!
            let { additionalFields } = this.params!

            let isValid = false;
            const isPassengerChangeName = additionalFields.find(d => d.type === "PASSENGER_NAME_CHANGE")
            if (isPassengerChangeName) {
                isValid = true
            } else {
                checkRequiredValidationAction.setParams({
                    additionalFields,
                    formData,
                    bookingId: "", // No need to used
                    selectedCategoryAndSubCategory: { category: null, subCategory: null } // no need to used
                });
                await checkRequiredValidationAction.execute()
                if (isActionRunSuccessfully(checkRequiredValidationAction)) {
                    const result = checkRequiredValidationAction.result!
                    isValid = result?.data || false
                }
            }
            if (isValid) {
                const isCommentFiled = additionalFields.find(d => d.title === "COMMENT")
                if (isCommentFiled) {
                    additionalFields = additionalFields.filter(d => d.title !== "COMMENT")
                }
                /*
                 * If we have a SELECT filed and it not required then we have value empty of that filed.
                 * But API does not allow "" value to the SELECT filed value.
                 * So need to removed all the filed which is SELECT and value is "".
                */
                const selectTypeFields = additionalFields.filter(f => f.type === "SELECT")
                if (selectTypeFields.length > 0) {
                    selectTypeFields.forEach(selectField => {
                        if (isEmpty(formData[selectField.fieldId])) {
                            additionalFields = additionalFields.filter(f => f.fieldId !== selectField.fieldId)
                        }
                    })
                }

                let fields: Partial<IAdditionalField>[] = []
                if (isPassengerChangeName) {
                    const passengers: any = getRecoilStateValue("modifiedPassengerNameAtom") || {}
                    if (Object.keys(passengers).length > 0) {
                        Object.keys(passengers).forEach((passengerId, index: number) => {
                            fields.push({
                                title: "CURRENT_NAME",
                                value: passengers[passengerId]?.oldName || "",
                                key: `${index + 1}`
                            })
                            fields.push({
                                title: "NEW_NAME",
                                value: `${passengers[passengerId]?.firstName || ""} ${passengers[passengerId]?.lastName || ""}`,
                                key: `${index + 1}`
                            })
                        })
                    }

                } else {
                    /**
                     * If we have a PASSENGER_FIRST_NAME or PASSENGER_LAST_NAME field type
                     * then we need to pass it into PASSENGER_NAME field type.
                     * And need to removed from the additionalFields array.                     
                    */
                    const passengerFirstNameField = additionalFields.find(d => d.title === "PASSENGER_FIRST_NAME")
                    const passengerLastNameField = additionalFields.find(d => d.title === "PASSENGER_LAST_NAME")
                    if (passengerFirstNameField && passengerLastNameField) {
                        additionalFields = additionalFields.filter(d => d.title !== "PASSENGER_FIRST_NAME" && d.title !== "PASSENGER_LAST_NAME")
                        additionalFields.push({
                            ...passengerFirstNameField,
                            title: "NAME",
                            type: "PASSENGER_NAME"
                        })
                    }


                    const isGroupBookingForm = selectedCategoryAndSubCategory?.category === "GROUP_BOOKING_FORM" && selectedCategoryAndSubCategory?.subCategory === "GROUP_BOOKING";
                    
                    const passengerTypes: IPassengerSelection[] = isGroupBookingForm ? (getRecoilStateValue<IPassengerSelection[]>("passengerTypesStateAtom") || []): [];

                    fields = additionalFields.map(({ ...field }: IAdditionalField) => {
                        const fieldValue: any = {
                            ...field,
                            value: "",
                        };
                        
                        if (isGroupBookingForm && field.type === "PASSENGER_TYPE" && formData[field.fieldId]) {
                            try {
                                const passengerData = JSON.parse(formData[field.fieldId]);
                                const transformedPassengerData: Record<string, number> = {};
                                
                                const passengerTypeMap: Record<string, string> = {};
                                passengerTypes.forEach((pt: IPassengerSelection) => {
                                    passengerTypeMap[pt.id] = pt.value;
                                });
                                
                                Object.keys(passengerData).forEach((key: string) => {
                                    const uppercaseKey = passengerTypeMap[key] || key.toUpperCase();
                                    transformedPassengerData[uppercaseKey] = passengerData[key];
                                });
                                
                                fieldValue.value = JSON.stringify(transformedPassengerData);
                            } catch (error) {
                                fieldValue.value = formData[field.fieldId];
                            }
                        } else if (field.type === "MOBILE") {
                            let phoneCode = formData["mobileCountryCode"] || "+971"
                            fieldValue.value = `${phoneCode}${formData[field.fieldId]}`
                        } else {
                            if (formData[field.fieldId])
                                fieldValue.value = formData[field.fieldId];
                        }
                        return fieldValue;
                    });
                    if (passengerFirstNameField && passengerLastNameField) {
                        fields = fields.map(d => {
                            if (d.title === "NAME") {
                                return {
                                    ...d,
                                    value: `${formData[passengerFirstNameField.fieldId] || ""} ${formData[passengerLastNameField.fieldId] || ""}`
                                }
                            }
                            return d
                        })
                    }
                }                
                const payload: ISaveServiceRequestPayload = {
                    type: "SERVICE_REQUEST",
                    subject: selectedCategoryAndSubCategory?.subCategory || "",
                    tags: [selectedCategoryAndSubCategory?.category || ""],
                    bookingId: bookingId || "",
                    additionalFields: [...fields],
                };
                if (isCommentFiled) {
                    payload.content = formData[isCommentFiled.fieldId] || ""
                }
                const response: any = await postServiceRequest(payload);
                if (response.status === "ok" && response.answer) {
                    let serviceRequest: ISaveServiceRequestResponse = response?.answer || []
                    updateRecoilState("saveServiceRequestResponseAtom", serviceRequest)
                    updateRecoilState("serviceRequestModalAtom", true)
                }
            }
            actionResult.status = "success"
        } catch (error) {
            actionResult.message = "Something went wrong"
        } finally {
            return actionResult
        }
    }
}

export const saveServiceRequestAction = new SaveServiceRequestAction()